import { decomposeColor, recomposeColor, Theme } from '@mui/material'
import { Table } from '@tanstack/react-table'

const checkColumnSize = (input?: number) => {
  if (!input) {
    return
  }

  if (input === Infinity) {
    return '1fr'
  }

  return `${input}px`
}

export const getColumnWidth = <T>({
  tableInstance,
  enableSelection,
  enableActionColumn,
}: {
  tableInstance: Table<T>
  enableSelection?: boolean
  enableActionColumn?: boolean
}) => ({
  gridTemplateColumns: `${enableSelection ? '48px ' : ''}${tableInstance.getAllLeafColumns().reduce((acc, col) => {
    const minWidth = checkColumnSize(col.columnDef.meta?.sizing?.minWidth)
    const maxWidth = minWidth ? checkColumnSize(col.columnDef.meta?.sizing?.maxWidth) || '1fr' : undefined
    const width = checkColumnSize(col.columnDef.meta?.sizing?.width)

    if (minWidth) {
      return `${acc} minmax(${minWidth}, ${maxWidth})`
    }

    if (width) {
      return `${acc} ${width}`
    }

    return `${acc} 1fr`
  }, '')} ${enableActionColumn ? ' 64px' : ''}`,
})

export const fetchMoreOnBottomReached = async ({
  containerRefElement,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  onTrigger,
}: {
  containerRefElement?: HTMLDivElement | null
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  fetchNextPage?: () => Promise<unknown>
  onTrigger?: () => void
}) => {
  if (containerRefElement) {
    const { scrollHeight, scrollTop, clientHeight } = containerRefElement
    if (scrollHeight - scrollTop - clientHeight < 300 && !isFetchingNextPage && hasNextPage && fetchNextPage) {
      await fetchNextPage()
      onTrigger?.()
    }
  }
}

export const fetchMoreOnTopReached = async ({
  containerRefElement,
  hasPreviousPage,
  isFetchingPreviousPage,
  fetchPreviousPage,
  onTrigger,
}: {
  containerRefElement?: HTMLDivElement | null
  hasPreviousPage?: boolean
  isFetchingPreviousPage?: boolean
  fetchPreviousPage?: () => Promise<unknown>
  onTrigger?: () => void
}) => {
  if (containerRefElement) {
    const { scrollHeight, scrollTop } = containerRefElement
    if (scrollHeight + scrollTop < 300 && !isFetchingPreviousPage && hasPreviousPage && fetchPreviousPage) {
      await fetchPreviousPage()
      onTrigger?.()
    }
  }
}

const blend = (background: string, overlay: string, opacity: number) => {
  const backgroundValues = decomposeColor(background).values
  const overlayValues = decomposeColor(overlay).values

  return recomposeColor({
    type: 'rgb',
    values: [0, 1, 2].map((index) =>
      Math.round(backgroundValues[index] + (overlayValues[index] - backgroundValues[index]) * opacity),
    ) as [number, number, number],
  })
}

export const getRowBackgroundColor = ({
  theme,
  isSelected,
  isHovered,
  tint,
}: {
  theme: Theme
  isSelected?: boolean
  isHovered?: boolean
  // A banding color for rows a list wants to set apart structurally (weekend rows, say). It stands
  // in for the row's own background, with hover and selection applied on top of it.
  tint?: string
}) => {
  const base = tint ?? theme.palette.background.levels[300]
  // Hover lifts a tinted row off its tint, so it still answers the pointer; a plain row keeps the
  // hover colour every other table uses.
  const hovered = tint ? blend(tint, theme.palette.background.levels[100], 0.6) : theme.palette.background.levels[100]
  const grounded = isHovered ? hovered : base

  return isSelected ? blend(grounded, theme.palette.primary.main, isHovered ? 0.28 : 0.2) : grounded
}

// Opaque on purpose: this is a border-bottom painted over the row's own background, so a
// translucent color would let the selection tint bleed into the gap between rows.
export const getRowSeparatorColor = ({ theme }: { theme: Theme }) =>
  blend(theme.palette.background.levels[300], theme.palette.background.levels[900], 0.3)

export const getRowDividerColor = ({ theme, isSelected }: { theme: Theme; isSelected?: boolean }) => {
  const base = theme.palette.background.levels[100]

  return isSelected ? blend(base, theme.palette.primary.main, 0.2) : base
}
